import type {ComponentType} from 'react';
import {useEffect, useState} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import {useHeadlessMode} from 'src/shared/hooks/useHeadlessMode';
import type {BrowserCompatibilityResult} from 'src/shared/utils/browserCompatibility';
import {checkBrowserCompatibility} from 'src/shared/utils/browserCompatibility';

export type StartupCheckResult = BrowserCompatibilityResult;

type BrowserIssuePageProps = {
  browserInfo?: BrowserCompatibilityResult['browserInfo'];
  originalUrl: string;
};

export interface StartupCheckOutletProps {
  children?: React.ReactNode;
  LoadingPage: ComponentType<{title?: string; message?: string}>;
  EmbeddedBrowserPage: ComponentType<BrowserIssuePageProps>;
  IncompatibleBrowserPage: ComponentType<BrowserIssuePageProps>;
  UnhandledErrorPage: ComponentType<{error: string}>;
}

export const StartupCheckOutlet = ({
  LoadingPage,
  EmbeddedBrowserPage,
  IncompatibleBrowserPage,
  UnhandledErrorPage,
}: StartupCheckOutletProps) => {
  const [checkResult, setCheckResult] = useState<StartupCheckResult | null>(
    null
  );
  const [isChecking, setIsChecking] = useState(true);
  const [originalUrl, setOriginalUrl] = useState<string>('');
  const location = useLocation();

  // Initialize headless mode from URL param or sessionStorage
  useHeadlessMode();

  useEffect(() => {
    const performCheck = () => {
      try {
        // Store the original URL when we first load
        if (!originalUrl) {
          setOriginalUrl(location.pathname + location.search);
        }

        const result = checkBrowserCompatibility();
        setCheckResult(result);
      } catch (error) {
        setCheckResult({
          isCompatible: false,
          issue: 'other',
          details: `Check failed: ${error}`,
        });
      } finally {
        setIsChecking(false);
      }
    };

    // Perform the check
    performCheck();
  }, [location.pathname, location.search, originalUrl]);

  // Show loading while checking
  if (isChecking) {
    return (
      <LoadingPage
        title="Checking browser compatibility..."
        message="We're verifying your browser supports all required features."
      />
    );
  }

  if (!checkResult?.isCompatible) {
    switch (checkResult?.issue) {
      case 'embedded-browser':
        return (
          <EmbeddedBrowserPage
            browserInfo={checkResult.browserInfo}
            originalUrl={originalUrl}
          />
        );
      case 'incompatible-browser':
        return (
          <IncompatibleBrowserPage
            browserInfo={checkResult.browserInfo}
            originalUrl={originalUrl}
          />
        );
      default:
        return (
          <UnhandledErrorPage
            error={checkResult?.details || 'Unknown compatibility issue'}
          />
        );
    }
  }

  // If compatible, render the outlet
  return <Outlet />;
};
